import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { Alert, Box, Grid, Typography } from '@material-ui/core';
import { connect } from 'react-redux';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import { onGetCD } from 'src/store/actions/global';
import { SearchByFilters } from './SearchByFilters';
import { AttentionByEntityChart } from './AttentionByEntityChart';
import { AttentionDurationChart } from './AttentionDurationChart';
import { ApplicationsByStateChart } from './ApplicationsByStateChart';
import { ApplicationsPendingStartChart } from './ApplicationsPendingStartChart';
import { ExtortedApplicationsCountChart } from './ExtortedApplicationsCountChart';
import { ExtortedApplicationsAmountChart } from './ExtortedApplicationsAmountChart';
import { ApplicationsByStateTable } from './ApplicationsByStateTable';
import { ApplicationsByStateWithDurationTable } from './ApplicationsByStateWithDurationTable';
import type {
  DashboardData,
  DashboardFiltersForm,
  DashboardLoadingState,
  DashboardOptions,
} from './types';
import {
  buildDashboardPayload,
  buildMonthOptions,
  getErrorMessage,
  getInitialDashboardData,
  getStaticPaymentChannelOptions,
  getStaticPlaceOptions,
  normalizeAttentionByPlace,
  normalizeDashboardSummary,
  normalizeNameValueSeries,
  normalizeOptions,
  normalizePlaceComparison,
  normalizeStatusEffectivityTable,
  transformAndSort,
  transformAttentionByEntityData,
} from './utils';

const getInitialFilters = (): DashboardFiltersForm => ({
  startDate: new Date(),
  endDate: new Date(),
  mesAnio: '',
  convenio: '',
  plaza: '',
  fuvexEmpresa: '',
  equipo: '',
  entidad: '',
  canalPago: '',
});

const getInitialOptions = (): DashboardOptions => ({
  mesAnio: buildMonthOptions(),
  convenio: [],
  plaza: getStaticPlaceOptions(),
  fuvexEmpresa: [],
  equipo: [],
  entidad: [],
  canalPago: getStaticPaymentChannelOptions(),
});

const getInitialLoadingState = (): DashboardLoadingState => ({
  catalogs: false,
  attentionByPlace: false,
  averageAmountByPlace: false,
  disbursementByStatus: false,
  agreementsToStart: false,
  financialInstitutionsToStart: false,
  agreementsExtort: false,
  financialInstitutionsExtort: false,
  totalAmountExtortByAgreement: false,
  totalAmountExtortByFinancialInstitution: false,
  totalApplicationsAndEffectivityByStatus: false,
  dashboardSummary: false,
});

const DashboardCompraDeuda: FC<any> = ({ onGetCD: onGetExec }) => {
  const isMountedRef = useIsMountedRef();
  const [messageErrorAttentionByEntityChart, setMessageErrorAttentionByEntityChart] = useState('');
  const [messageErrorAverageAmountByPlace, setMessageErrorAverageAmountByPlace] = useState('');
  const [messageErrorDisbursementByStatus, setMessageErrorDisbursementByStatus] = useState('');
  const [messageErrorAgreementsToStart, setMessageErrorAgreementsToStart] = useState('');
  const [messageErrorFinancialInstitutionsToStart, setMessageErrorFinancialInstitutionsToStart] = useState('');
  const [messageErrorAgreementsExtort, setMessageErrorAgreementsExtort] = useState('');
  const [messageErrorFinancialInstitutionsExtort, setMessageErrorFinancialInstitutionsExtort] = useState('');
  const [messageErrorTotalAmountExtortByAgreement, setMessageErrorTotalAmountExtortByAgreement] = useState('');
  const [messageErrorTotalAmountExtortByFinancialInstitution, setMessageErrorTotalAmountExtortByFinancialInstitution] = useState('');
  const [messageErrorTotalApplicationsAndEffectivityByStatus, setMessageErrorTotalApplicationsAndEffectivityByStatus] = useState('');
  const [messageErrorDashboardSummary, setMessageErrorDashboardSummary] = useState('');
  const [formFilter, setFormFilter] = useState<DashboardFiltersForm>(getInitialFilters());
  const [options, setOptions] = useState<DashboardOptions>(getInitialOptions());
  const [data, setData] = useState<DashboardData>(getInitialDashboardData());
  const [loading, setLoading] = useState<DashboardLoadingState>(getInitialLoadingState());

  const changeFilter = (name, value) => {
    setFormFilter((values) => ({
      ...values,
      [name]: value,
    }));
  };

  const loadCatalogs = async () => {
    setLoading((values) => ({ ...values, catalogs: true }));

    const requests = [
      onGetExec({ url: '/cd-dashboard/all-business-leaders', global: false }),
      onGetExec({ url: '/cd-dashboard/fuvex-companies', global: false }),
      onGetExec({ url: '/cd-dashboard/agreements', global: false }),
      onGetExec({ url: '/cd-dashboard/financial-institutions', global: false }),
    ];

    const [businessLeadersRes, fuvexCompaniesRes, agreementsRes, financialInstitutionsRes] = await Promise.allSettled(requests);

    const businessLeaders = businessLeadersRes.status === 'fulfilled' ? businessLeadersRes.value : null;
    const fuvexCompanies = fuvexCompaniesRes.status === 'fulfilled' ? fuvexCompaniesRes.value : null;
    const agreements = agreementsRes.status === 'fulfilled' ? agreementsRes.value : null;
    const financialInstitutions = financialInstitutionsRes.status === 'fulfilled' ? financialInstitutionsRes.value : null;

    if (businessLeaders === null || fuvexCompanies === null || agreements === null || financialInstitutions === null) {
      return;
    }

    if (isMountedRef.current) {
      setOptions((prev) => ({
        ...prev,
        mesAnio: buildMonthOptions(),
        convenio: normalizeOptions(agreements),
        plaza: getStaticPlaceOptions(),
        fuvexEmpresa: normalizeOptions(fuvexCompanies),
        equipo: normalizeOptions(businessLeaders),
        entidad: normalizeOptions(financialInstitutions),
        canalPago: getStaticPaymentChannelOptions(),
      }));

      setLoading((values) => ({ ...values, catalogs: false }));
    }
  };

  const loadDashboard = async (filters: DashboardFiltersForm) => {
    setMessageErrorAttentionByEntityChart('');
    setMessageErrorAverageAmountByPlace('');
    setMessageErrorDisbursementByStatus('');
    setMessageErrorAgreementsToStart('');
    setMessageErrorFinancialInstitutionsToStart('');
    setMessageErrorAgreementsExtort('');
    setMessageErrorFinancialInstitutionsExtort('');
    setMessageErrorTotalAmountExtortByAgreement('');
    setMessageErrorTotalAmountExtortByFinancialInstitution('');
    setMessageErrorTotalApplicationsAndEffectivityByStatus('');
    setMessageErrorDashboardSummary('');
    setLoading((values) => ({
      ...values,
      attentionByPlace: true,
      averageAmountByPlace: true,
      disbursementByStatus: true,
      agreementsToStart: true,
      financialInstitutionsToStart: true,
      agreementsExtort: true,
      financialInstitutionsExtort: true,
      totalAmountExtortByAgreement: true,
      totalAmountExtortByFinancialInstitution: true,
      totalApplicationsAndEffectivityByStatus: true,
      dashboardSummary: true,
    }));

    const payload = buildDashboardPayload(filters);
    // Launch each request in parallel and handle results individually so
    // partial data can be shown as it arrives and single failures don't fail all.
    const runAttentionByPlace = (async () => {
      try {
        const res = await onGetExec({ url: '/cd-dashboard/financial-institutions-attentions/by-place', body: payload, global: false });
        if (isMountedRef.current) {
          setData((prev) => ({ ...prev, attentionByPlace: normalizeAttentionByPlace(transformAttentionByEntityData(res)) }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          setMessageErrorAttentionByEntityChart((prev) => prev || getErrorMessage('Error al cargar datos'));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading((v) => ({ ...v, attentionByPlace: false }));
        }
      }
    })();

    const runAverageAmountByPlace = (async () => {
      try {
        const res = await onGetExec({ url: '/cd-dashboard/average-amount-by-place', body: payload, global: false });
        if (isMountedRef.current) {
          setData((prev) => ({ ...prev, averageAmountByPlace: normalizePlaceComparison(res) }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          setMessageErrorAverageAmountByPlace((prev) => prev || getErrorMessage('Error al cargar datos'));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading((v) => ({ ...v, averageAmountByPlace: false }));
        }
      }
    })();

    const runDisbursementByStatus = (async () => {
      try {
        const res = await onGetExec({ url: '/cd-dashboard/disbursement-applications/by-status', body: payload, global: false });
        if (isMountedRef.current) {
          setData((prev) => ({ ...prev, disbursementByStatus: normalizeNameValueSeries(transformAndSort(res), ['cantidad', 'count', 'total']) }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          setMessageErrorDisbursementByStatus((prev) => prev || getErrorMessage('Error al cargar datos'));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading((v) => ({ ...v, disbursementByStatus: false }));
        }
      }
    })();

    const runAgreementsToStart = (async () => {
      try {
        const res = await onGetExec({ url: '/cd-dashboard/agreements-applications/to-start', body: payload, global: false });
        if (isMountedRef.current) {
          setData((prev) => ({ ...prev, agreementsToStart: normalizeNameValueSeries(Object.values(res).map((item: any) => ({ name: item.convenio, total: item.solicitudes_totales })), ['cantidad', 'count', 'total']) }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          setMessageErrorAgreementsToStart((prev) => prev || getErrorMessage('Error al cargar datos'));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading((v) => ({ ...v, agreementsToStart: false }));
        }
      }
    })();

    const runFinancialInstitutionsToStart = (async () => {
      try {
        const res = await onGetExec({ url: '/cd-dashboard/finantial-institutions-applications/to-start', body: payload, global: false });
        if (isMountedRef.current) {
          setData((prev) => ({ ...prev, financialInstitutionsToStart: normalizeNameValueSeries(Object.values(res).map((item: any) => ({ name: item.institucion_financiera, total: item.solicitudes_totales })), ['cantidad', 'count', 'total']) }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          setMessageErrorFinancialInstitutionsToStart((prev) => prev || getErrorMessage('Error al cargar datos'));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading((v) => ({ ...v, financialInstitutionsToStart: false }));
        }
      }
    })();

    const runAgreementsExtort = (async () => {
      try {
        const res = await onGetExec({ url: '/cd-dashboard/agreements-applications/extort', body: payload, global: false });
        if (isMountedRef.current) {
          setData((prev) => ({ ...prev, agreementsExtort: normalizeNameValueSeries(Object.values(res).map((item: any) => ({ name: item.convenio, total: item.solicitudes_totales })), ['cantidad', 'count', 'total']) }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          setMessageErrorAgreementsExtort((prev) => prev || getErrorMessage('Error al cargar datos'));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading((v) => ({ ...v, agreementsExtort: false }));
        }
      }
    })();

    const runFinancialInstitutionsExtort = (async () => {
      try {
        const res = await onGetExec({ url: '/cd-dashboard/financial-institutions-applications/extort', body: payload, global: false });
        if (isMountedRef.current) {
          setData((prev) => ({ ...prev, financialInstitutionsExtort: normalizeNameValueSeries(Object.values(res).map((item: any) => ({ name: item.convenio, total: item.solicitudes_totales })), ['cantidad', 'count', 'total']) }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          setMessageErrorFinancialInstitutionsExtort((prev) => prev || getErrorMessage('Error al cargar datos'));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading((v) => ({ ...v, financialInstitutionsExtort: false }));
        }
      }
    })();

    const runTotalAmountExtortByAgreement = (async () => {
      try {
        const res = await onGetExec({ url: '/cd-dashboard/agreements/total-amount-extort', body: payload, global: false });
        if (isMountedRef.current) {
          setData((prev) => ({ ...prev, totalAmountExtortByAgreement: normalizeNameValueSeries(Object.values(res).map((item: any) => ({ name: item.convenio, total: item.monto_total })), ['cantidad', 'count', 'total']) }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          setMessageErrorTotalAmountExtortByAgreement((prev) => prev || getErrorMessage('Error al cargar datos'));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading((v) => ({ ...v, totalAmountExtortByAgreement: false }));
        }
      }
    })();

    const runTotalAmountExtortByFinancialInstitution = (async () => {
      try {
        const res = await onGetExec({ url: '/cd-dashboard/financial-institutions/total-amount-extort', body: payload, global: false });
        if (isMountedRef.current) {
          setData((prev) => ({ ...prev, totalAmountExtortByFinancialInstitution: normalizeNameValueSeries(Object.values(res).map((item: any) => ({ name: item.convenio, total: item.monto_total })), ['cantidad', 'count', 'total']) }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          setMessageErrorTotalAmountExtortByFinancialInstitution((prev) => prev || getErrorMessage('Error al cargar datos'));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading((v) => ({ ...v, totalAmountExtortByFinancialInstitution: false }));
        }
      }
    })();

    const runTotalApplicationsAndEffectivityByStatus = (async () => {
      try {
        const res = await onGetExec({ url: '/cd-dashboard/total-applications-and-efectivity-by-status', body: payload, global: false });
        if (isMountedRef.current) {
          setData((prev) => ({ ...prev, totalApplicationsAndEffectivityByStatus: normalizeStatusEffectivityTable(res) }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          setMessageErrorTotalApplicationsAndEffectivityByStatus((prev) => prev || getErrorMessage('Error al cargar datos'));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading((v) => ({ ...v, totalApplicationsAndEffectivityByStatus: false }));
        }
      }
    })();

    const runDashboardSummary = (async () => {
      try {
        const res = await onGetExec({ url: '/cd-dashboard/compra-deuda-dashboard-summary', body: payload, global: false });
        if (isMountedRef.current) {
          setData((prev) => ({ ...prev, dashboardSummary: normalizeDashboardSummary(res) }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          setMessageErrorDashboardSummary((prev) => prev || getErrorMessage('Error al cargar datos'));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading((v) => ({ ...v, dashboardSummary: false }));
        }
      }
    })();

    // Wait for all to finish before resolving so callers know when loadDashboard completes.
    await Promise.all([
      runAttentionByPlace,
      runAverageAmountByPlace,
      runDisbursementByStatus,
      runAgreementsToStart,
      runFinancialInstitutionsToStart,
      runAgreementsExtort,
      runFinancialInstitutionsExtort,
      runTotalAmountExtortByAgreement,
      runTotalAmountExtortByFinancialInstitution,
      runTotalApplicationsAndEffectivityByStatus,
      runDashboardSummary,
    ]);
  };

  const onSubmit = async () => {
    if (isMountedRef.current) {
      try {
        await loadDashboard(formFilter);
      } catch (error) {
        if (isMountedRef.current) {
          setLoading(getInitialLoadingState());
        }
      }
    }
  };

  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        await loadCatalogs();
        await loadDashboard(getInitialFilters());
      } catch (error) {
        if (isMountedRef.current) {
          setLoading(getInitialLoadingState());
        }
      }
    };

    initializeDashboard();
  }, []);

  const isAnyLoading = Object.values(loading).some(Boolean);

  return (
    <Box mt={2} my={5}>
      <Grid container spacing={3} rowSpacing={3}>
        <Grid item xs={12}>
          <SearchByFilters
            onSubmit={onSubmit}
            formFilter={formFilter}
            changeFilter={changeFilter}
            options={options}
            loading={isAnyLoading}
          />
        </Grid>

        <Grid item xs={12} lg={12} xl={8}>
          { messageErrorAttentionByEntityChart === '' && (
          <AttentionByEntityChart
            limaData={data.attentionByPlace.lima}
            provinciaData={data.attentionByPlace.provincia}
            loading={loading.attentionByPlace}
          />
          )}
          {messageErrorAttentionByEntityChart && (
          <Grid item xs={12}>
            <Typography
              component="h2"
              style={{
                textAlign: 'center',
                margin: '0 0 18px',
                fontSize: 18,
                fontWeight: 700,
                color: '#4a4a4a',
                letterSpacing: 1,
              }}
            >
              ATENCION POR ENTIDAD
            </Typography>

            <div style={{ width: 'auto', height: '380px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Alert severity="error">{messageErrorAttentionByEntityChart}</Alert>
            </div>
          </Grid>
          )}
        </Grid>

        <Grid item xs={12} lg={6} xl={4}>
          { messageErrorAverageAmountByPlace === '' && (
          <AttentionDurationChart
            data={data.averageAmountByPlace}
            loading={loading.averageAmountByPlace}
          />
          )}
          {messageErrorAverageAmountByPlace && (
            <Grid item xs={12}>
              <Typography
                component="h2"
                style={{
                  textAlign: 'center',
                  margin: '0 0 18px',
                  fontSize: 18,
                  fontWeight: 700,
                  color: '#4a4a4a',
                  letterSpacing: 1,
                }}
              >
                TIEMPO DE ATENCIÓN
              </Typography>
              <div style={{ width: 'auto', height: '380px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Alert severity="error">{messageErrorAverageAmountByPlace}</Alert>
              </div>
            </Grid>
          )}
        </Grid>

        <Grid item xs={12} lg={6} xl={6}>
          { messageErrorDisbursementByStatus === '' && (
          <ApplicationsByStateChart
            data={data.disbursementByStatus}
            loading={loading.disbursementByStatus}
          />
          )}
          {messageErrorDisbursementByStatus && (
          <Grid item xs={12}>
            <Typography
              component="h2"
              style={{
                textAlign: 'center',
                margin: '0 0 18px',
                fontSize: 18,
                fontWeight: 700,
                color: '#4a4a4a',
                letterSpacing: 1,
              }}
            >
              SOLICITUDES DE DESEMBOLSO POR ESTADO
            </Typography>
            <div style={{ width: 'auto', height: '380px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Alert severity="error">{messageErrorDisbursementByStatus}</Alert>
            </div>
          </Grid>
          )}
        </Grid>

        <Grid item xs={12} lg={6} xl={3}>
          { messageErrorAgreementsToStart === '' && (
          <ApplicationsPendingStartChart
            data={data.agreementsToStart}
            loading={loading.agreementsToStart}
            title="SOLICITUDES POR INICIAR POR CONVENIO"
          />
          )}
          {messageErrorAgreementsToStart && (
          <Grid item xs={12}>
            <Typography
              component="h2"
              style={{
                textAlign: 'center',
                margin: '0 0 18px',
                fontSize: 18,
                fontWeight: 700,
                color: '#4a4a4a',
                letterSpacing: 1,
              }}
            >
              SOLICITUDES POR INICIAR POR CONVENIO
            </Typography>
            <div style={{ width: 'auto', height: '380px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Alert severity="error">{messageErrorAgreementsToStart}</Alert>
            </div>
          </Grid>
          )}
        </Grid>

        <Grid item xs={12} lg={6} xl={3}>
          { messageErrorFinancialInstitutionsToStart === '' && (
          <ExtortedApplicationsCountChart
            data={data.financialInstitutionsToStart}
            loading={loading.financialInstitutionsToStart}
            title="SOLICITUDES POR INICIAR POR ENTIDAD"
          />
          )}
          {messageErrorFinancialInstitutionsToStart && (
          <Grid item xs={12}>
            <Typography
              component="h2"
              style={{
                textAlign: 'center',
                margin: '0 0 18px',
                fontSize: 18,
                fontWeight: 700,
                color: '#4a4a4a',
                letterSpacing: 1,
              }}
            >
              SOLICITUDES POR INICIAR POR ENTIDAD
            </Typography>
            <div style={{ width: 'auto', height: '380px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Alert severity="error">{messageErrorFinancialInstitutionsToStart}</Alert>
            </div>
          </Grid>
          )}
        </Grid>

        <Grid item xs={12} lg={6} xl={3}>
          { messageErrorAgreementsExtort === '' && (
          <ExtortedApplicationsCountChart
            data={data.agreementsExtort}
            loading={loading.agreementsExtort}
            title="SOLICITUDES EXTORNADAS POR CONVENIO"
          />
          )}
          {messageErrorAgreementsExtort && (
          <Grid item xs={12}>
            <Typography
              component="h2"
              style={{
                textAlign: 'center',
                margin: '0 0 18px',
                fontSize: 18,
                fontWeight: 700,
                color: '#4a4a4a',
                letterSpacing: 1,
              }}
            >
              SOLICITUDES EXTORNADAS POR CONVENIO
            </Typography>
            <div style={{ width: 'auto', height: '380px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Alert severity="error">{messageErrorAgreementsExtort}</Alert>
            </div>
          </Grid>
          )}
        </Grid>

        <Grid item xs={12} lg={6} xl={3}>
          { messageErrorFinancialInstitutionsExtort === '' && (
          <ExtortedApplicationsCountChart
            data={data.financialInstitutionsExtort}
            loading={loading.financialInstitutionsExtort}
            title="SOLICITUDES EXTORNADAS POR ENTIDAD"
          />
          )}
          {messageErrorFinancialInstitutionsExtort && (
          <Grid item xs={12}>
            <Typography
              component="h2"
              style={{
                textAlign: 'center',
                margin: '0 0 18px',
                fontSize: 18,
                fontWeight: 700,
                color: '#4a4a4a',
                letterSpacing: 1,
              }}
            >
              SOLICITUDES EXTORNADAS POR ENTIDAD
            </Typography>
            <div style={{ width: 'auto', height: '380px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Alert severity="error">{messageErrorFinancialInstitutionsExtort}</Alert>
            </div>
          </Grid>
          )}
        </Grid>

        <Grid item xs={12} lg={6} xl={3}>
          { messageErrorTotalAmountExtortByAgreement === '' && (
          <ExtortedApplicationsAmountChart
            data={data.totalAmountExtortByAgreement}
            loading={loading.totalAmountExtortByAgreement}
            title="MONTO EXTORNADO POR CONVENIO"
          />
          )}
          {messageErrorTotalAmountExtortByAgreement && (
          <Grid item xs={12}>
            <Typography
              component="h2"
              style={{
                textAlign: 'center',
                margin: '0 0 18px',
                fontSize: 18,
                fontWeight: 700,
                color: '#4a4a4a',
                letterSpacing: 1,
              }}
            >
              MONTO EXTORNADO POR CONVENIO
            </Typography>
            <div style={{ width: 'auto', height: '380px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Alert severity="error">{messageErrorTotalAmountExtortByAgreement}</Alert>
            </div>
          </Grid>
          )}
        </Grid>

        <Grid item xs={12} lg={6} xl={3}>
          { messageErrorTotalAmountExtortByFinancialInstitution === '' && (
          <ExtortedApplicationsAmountChart
            data={data.totalAmountExtortByFinancialInstitution}
            loading={loading.totalAmountExtortByFinancialInstitution}
            title="MONTO EXTORNADO POR ENTIDAD"
          />
          )}
          {messageErrorTotalAmountExtortByFinancialInstitution && (
          <Grid item xs={12}>
            <Typography
              component="h2"
              style={{
                textAlign: 'center',
                margin: '0 0 18px',
                fontSize: 18,
                fontWeight: 700,
                color: '#4a4a4a',
                letterSpacing: 1,
              }}
            >
              MONTO EXTORNADO POR ENTIDAD
            </Typography>
            <div style={{ width: 'auto', height: '380px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Alert severity="error">{messageErrorTotalAmountExtortByFinancialInstitution}</Alert>
            </div>
          </Grid>
          )}
        </Grid>

        <Grid item xs={12}>
          { messageErrorTotalApplicationsAndEffectivityByStatus === '' && (
          <ApplicationsByStateTable
            months={data.totalApplicationsAndEffectivityByStatus.months}
            rows={data.totalApplicationsAndEffectivityByStatus.rows}
            totalRow={data.totalApplicationsAndEffectivityByStatus.totalRow}
            loading={loading.totalApplicationsAndEffectivityByStatus}
          />
          )}
          {messageErrorTotalApplicationsAndEffectivityByStatus && (
          <Grid item xs={12}>
            <Typography
              component="h2"
              style={{
                textAlign: 'center',
                margin: '0 0 18px',
                fontSize: 18,
                fontWeight: 700,
                color: '#4a4a4a',
                letterSpacing: 1,
              }}
            >
              FECHA DE DESEMBOLSO / CANTIDAD / EFECTIVIDAD
            </Typography>
            <div style={{ width: 'auto', height: '380px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Alert severity="error">{messageErrorTotalApplicationsAndEffectivityByStatus}</Alert>
            </div>
          </Grid>
          )}
        </Grid>

        <Grid item xs={12}>
          { messageErrorDashboardSummary === '' && (
          <ApplicationsByStateWithDurationTable
            months={data.dashboardSummary.months}
            groups={data.dashboardSummary.groups}
            totalRow={data.dashboardSummary.totalRow}
            loading={loading.dashboardSummary}
          />
          )}
          {messageErrorDashboardSummary && (
          <div style={{ width: 'auto', margin: '0 auto' }}>
            <Typography
              component="h2"
              style={{
                textAlign: 'center',
                margin: '0 0 18px',
                fontSize: 18,
                fontWeight: 700,
                color: '#4a4a4a',
                letterSpacing: 1,
              }}
            >
              FECHA DE DESEMBOLSO / CANTIDAD / IMPORTE
            </Typography>
            <div style={{ width: 'auto', height: '380px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Alert severity="error">{messageErrorDashboardSummary}</Alert>
            </div>
          </div>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default connect(null, {
  onGetCD,
})(DashboardCompraDeuda);
